/**
 * Talking to the relay.
 *
 * Two behaviours here are not obvious and both cost real debugging time:
 *
 * 1. NIP-42. The relay answers the first REQ with `auth-required` rather than
 *    sending a challenge we can wait for, so the demand drives the handshake:
 *    try, get told, authenticate, retry. Waiting for the challenge up front is
 *    a race the client loses on a slow connection.
 *
 * 2. Gift wraps are back-dated. NIP-59 randomises `created_at` up to two days
 *    into the past to defeat timing analysis, so any `since` narrower than that
 *    silently hides new mail — it looks like an empty inbox, not an error.
 *    postrd back-dates less, but a DM from another client uses the full two.
 */
import { Relay, type Event as NostrEvent, type Filter, type VerifiedEvent } from "nostr-tools";
import type { MailSigner } from "./signer";

/** NIP-59's back-dating window, plus margin. Never filter tighter than this. */
export const GIFT_WRAP_SKEW_SECONDS = 49 * 60 * 60;

export const KIND_GIFT_WRAP = 1059;

export interface RelayOptions {
  url: string;
  pubkey: string;
}

type Signer = (draft: {
  kind: number;
  created_at: number;
  tags: string[][];
  content: string;
}) => Promise<VerifiedEvent>;

export class RelayConnection {
  private relay: Relay | null = null;
  private authed = false;

  constructor(private readonly opts: RelayOptions, private readonly sign: Signer) {}

  async connect(): Promise<Relay> {
    if (this.relay?.connected) return this.relay;
    this.relay = await Relay.connect(this.opts.url);
    this.authed = false;
    return this.relay;
  }

  private async authenticate(): Promise<void> {
    if (this.authed) return;
    const relay = await this.connect();
    await relay.auth((draft) => this.sign(draft));
    this.authed = true;
  }

  /**
   * Collect everything matching a filter up to end-of-stored-events.
   *
   * Authenticates on demand: the relay closes the subscription with
   * `auth-required`, we authenticate, and retry once.
   */
  async list(filter: Filter, timeoutMs = 20_000): Promise<NostrEvent[]> {
    try {
      return await this.collect(filter, timeoutMs);
    } catch (err) {
      if (!needsAuth(err)) throw err;
      await this.authenticate();
      return this.collect(filter, timeoutMs);
    }
  }

  private async collect(filter: Filter, timeoutMs: number): Promise<NostrEvent[]> {
    const relay = await this.connect();
    return new Promise<NostrEvent[]>((resolve, reject) => {
      const events: NostrEvent[] = [];
      const timer = setTimeout(() => {
        sub.close();
        resolve(events);
      }, timeoutMs);
      const sub = relay.subscribe([filter], {
        onevent: (e) => events.push(e),
        oneose: () => {
          clearTimeout(timer);
          sub.close();
          resolve(events);
        },
        onclose: (reason) => {
          clearTimeout(timer);
          if (reason && /auth-required|restricted/.test(reason)) {
            reject(new Error(reason));
          } else {
            resolve(events);
          }
        },
      });
    });
  }

  /**
   * Stream events as they arrive. Returns an unsubscribe function.
   *
   * What the relay already held is replayed first and is not news, so nothing
   * is reported until end-of-stored-events. Authentication is on demand, as in
   * `list`, and `onClose` hears a dropped socket or a refusal, so the caller can
   * open another.
   */
  async watch(
    filter: Filter,
    onEvent: (e: NostrEvent) => void,
    onClose: (reason: string) => void = () => {},
  ): Promise<() => void> {
    const relay = await this.connect();
    let current: { close(): void } | null = null;
    let retried = false;
    const open = (): void => {
      let live = false;
      current = relay.subscribe([filter], {
        onevent: (e) => {
          if (live) onEvent(e);
        },
        oneose: () => {
          live = true;
        },
        onclose: (reason) => {
          if (!retried && needsAuth(reason)) {
            retried = true;
            this.authenticate().then(open, () => onClose(reason));
            return;
          }
          onClose(reason);
        },
      });
    };
    open();
    return () => current?.close();
  }

  async publish(event: NostrEvent): Promise<void> {
    const relay = await this.connect();
    try {
      await relay.publish(event);
    } catch (err) {
      if (!needsAuth(err)) throw err;
      await this.authenticate();
      await relay.publish(event);
    }
  }

  close(): void {
    this.relay?.close();
    this.relay = null;
    this.authed = false;
  }
}

export function needsAuth(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /auth-required/i.test(message);
}

/** Adapt a MailSigner to what the relay client expects. */
export function signerFor(signer: MailSigner): Signer {
  return (draft) => signer.signEvent(draft);
}

/** A filter for every gift wrap addressed to us, allowing for the back-dating. */
export function mailFilter(pubkey: string, sinceSeconds?: number): Filter {
  const filter: Filter = { kinds: [KIND_GIFT_WRAP], "#p": [pubkey] };
  if (sinceSeconds !== undefined) {
    filter.since = Math.max(0, sinceSeconds - GIFT_WRAP_SKEW_SECONDS);
  }
  return filter;
}
